// ---------------------------------------------------------------------------
// kg-client — feature-flag-gated wrapper around @cogo/kg-browser for Starter
//
// Phase 2a Step 5 (per .planning/wasm-client-migration-session-plan.md §6
// WASM Step 5):
//   Replaces the server-side mcp-gateway round-trip for THREE read-only tools
//   with a local Oxigraph WASM query. Fallback to server stays reachable
//   behind the VITE_KG_BROWSER flag so production rollout can gate cleanly.
//
// Same-origin advantage vs Studio (Step 4):
//   cogo-starter IS the CDN origin for the KG shards (they're published to
//   apps/cogo-starter/public/kg/ by scripts/publish-shard.mjs). The default
//   manifest URL is therefore `/kg/manifest.json` (same-origin), which avoids
//   a cross-origin CORS cascade entirely.
//
// The 3 read-only tools:
//   - search_components — text search over cogo:Component instances
//   - get_component     — single component lookup by IRI
//   - get_stats         — versionInfo + triple counts
//
// Write-path tools (publish_patterns / report_feedback / resolve) stay on
// cogo-mcp-gateway since writes touch Supabase + need server auth. Out of
// scope for Step 5 per plan doc.
// ---------------------------------------------------------------------------

import type {
  CoreCapabilities,
  InitResult,
  KgBrowserVersion,
  QueryShapeResult,
  ShaclQueryRule,
  ShaclReport,
} from '@cogo/kg-browser'

// ---------------------------------------------------------------------------
// Feature flag + CDN config
// ---------------------------------------------------------------------------

/**
 * VITE_KG_BROWSER feature flag.
 *   - "1" / "true" — enable local KG path
 *   - "0" / "false" / unset — fall through to server-side cogo-mcp-gateway
 *
 * Default OFF on first merge; flip ON after §5.5 preview browser verify.
 */
export function isKgBrowserEnabled(): boolean {
  const raw = import.meta.env.VITE_KG_BROWSER as string | undefined
  if (!raw) return false
  return raw === '1' || raw.toLowerCase() === 'true'
}

/**
 * CDN base for KG shards. Defaults to same-origin `/kg/manifest.json` because
 * cogo-starter publishes the shards itself via scripts/publish-shard.mjs into
 * `apps/cogo-starter/public/kg/`. Same-origin means no CORS cascade, no
 * extra connect-src entry needed, and a single Cache-Control honored by the
 * browser.
 *
 * Can be overridden via VITE_KG_MANIFEST_URL for alternative CDN or staging.
 */
export function getManifestUrl(): string {
  const raw = import.meta.env.VITE_KG_MANIFEST_URL as string | undefined
  return raw ?? '/kg/manifest.json'
}

/**
 * Cloudflare Worker base URL for observation sync/save (Gap 2).
 *
 * Resolution order:
 *   1. VITE_KG_WORKER_URL env var (any environment)
 *   2. In dev/test only — preview Worker URL fallback
 *   3. In prod (import.meta.env.PROD), throws — operators MUST set
 *      VITE_KG_WORKER_URL via Cloudflare Pages dashboard so observations
 *      land in the correct R2 bucket per environment.
 *
 * Verified live 2026-04-20 on preview at version 55957c86 (planned
 * docs/agentic/CHANGELOG.md v7.3). Mirror of the Studio helper —
 * intentional duplication; consolidate into a shared package once 4+
 * consumers exhibit the pattern.
 */
export function getWorkerBaseUrl(): string {
  const raw = import.meta.env.VITE_KG_WORKER_URL as string | undefined
  if (raw && raw.length > 0) return raw
  if (import.meta.env.PROD) {
    throw new Error(
      '[kg-client] VITE_KG_WORKER_URL is required in production builds — ' +
      'set it via Cloudflare Pages dashboard environment variables. ' +
      'The dev fallback (preview Worker) is intentionally disabled in prod ' +
      'to prevent observations from leaking into the wrong R2 bucket.',
    )
  }
  return 'https://cogo-kg-worker-preview.misterylee.workers.dev'
}

// ---------------------------------------------------------------------------
// Lazy init — only load kg-browser module when flag is ON
// ---------------------------------------------------------------------------

interface KgBrowserModule {
  initKgCore: (opts?: unknown) => Promise<InitResult>
  queryShape: (sparql: string) => QueryShapeResult
  applyShacl: (rules: ShaclQueryRule[]) => ShaclReport
  versionInfo: () => KgBrowserVersion
  detectCapabilities: () => CoreCapabilities
  fetchShard: (opts: unknown) => Promise<unknown>
  isReady: () => boolean
  getLastCapabilities: () => CoreCapabilities | null
}

let kgModule: KgBrowserModule | null = null
let initPromise: Promise<InitResult | null> | null = null
let initResult: InitResult | null = null

/**
 * Load the kg-browser module lazily. First call triggers the dynamic import
 * + init pipeline. Subsequent calls return the cached init result.
 *
 * Returns null if the feature flag is off OR if init failed. Callers MUST
 * treat null as "fall through to server path".
 */
export async function ensureKgReady(): Promise<InitResult | null> {
  if (!isKgBrowserEnabled()) return null
  if (initResult) return initResult
  if (initPromise) return initPromise

  initPromise = (async () => {
    try {
      const t0 = performance.now()

      // Dynamic import keeps kg-browser OUT of the initial bundle when flag is off.
      const mod = (await import('@cogo/kg-browser')) as unknown as KgBrowserModule
      kgModule = mod

      // Fetch manifest to discover shard URLs.
      const manifestUrl = getManifestUrl()
      const manifestRes = await fetch(manifestUrl)
      if (!manifestRes.ok) {
        throw new Error(
          `[kg-client] manifest fetch failed ${manifestRes.status} ${manifestRes.statusText} — ${manifestUrl}`,
        )
      }
      const manifest = (await manifestRes.json()) as {
        cdnBase: string
        entries: Array<{ layer: string; name: string; url: string; sha256: string }>
      }

      // Resolve entry URLs relative to the manifest location. This handles
      // both absolute (cross-origin) and relative (same-origin `/kg/...`)
      // manifest URLs identically.
      const base = new URL(manifestUrl, window.location.href)
      const urlFor = (entry: { url: string }) => new URL(entry.url, base).toString()

      const wasmEntry = manifest.entries.find((e) => e.layer === 'L0')
      const ttlEntries = manifest.entries.filter((e) => e.layer !== 'L0' && e.layer !== 'L1b')

      // Fetch TTL shards in parallel for hydration.
      const ttlSources = await Promise.all(
        ttlEntries.map(async (e) => {
          const res = await fetch(urlFor(e))
          if (!res.ok) throw new Error(`[kg-client] ttl fetch failed ${e.name}: ${res.status}`)
          return { name: e.name, ttl: await res.text() }
        }),
      )

      // Separate SHACL shapes TTL (cogo-ns-v1.ttl has the embedded NodeShapes).
      const shapesSource = ttlSources.find((t) => t.name === 'cogo-ns-v1.ttl')

      const result = await mod.initKgCore({
        wasmUrl: wasmEntry ? urlFor(wasmEntry) : undefined,
        ttlSources,
        shapesTtl: shapesSource?.ttl,
        verbose: import.meta.env.DEV,
      })
      initResult = result

      // eslint-disable-next-line no-console
      console.debug('[kg-client] initKgCore ready', {
        initMs: Math.round((performance.now() - t0) * 100) / 100,
        capabilities: result.capabilities,
        loadedTriples: result.loadedTriples,
        shapeSha: result.shapeSha.slice(0, 16),
      })

      return result
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[kg-client] init failed — falling back to server path', err)
      kgModule = null
      initResult = null
      return null
    }
  })()

  return initPromise
}

/** Read-only accessor for already-loaded module. Returns null if not ready. */
function getModule(): KgBrowserModule | null {
  return kgModule
}

// ---------------------------------------------------------------------------
// Typed wrappers for the 3 read-only tools
// ---------------------------------------------------------------------------

/**
 * Normalized ComponentInfo — intentionally aligned with the mcp-gateway
 * response shape so callers can drop-in replace.
 *
 * When the canonical mcp-gateway shape evolves, update this type + the
 * local mapping logic below; the server fallback path uses its own types
 * and is not affected.
 */
export interface ComponentInfo {
  iri: string
  name: string
  category?: string
  description?: string
}

export interface ComponentStats {
  totalComponents: number
  totalEvents: number
  totalActions: number
  shapeSha: string
  coreVersion: string
  oxigraphVersion: string
}

export interface KgClientLatencies {
  /** Milliseconds — last SPARQL query wall-clock duration. */
  lastQueryMs: number
}

const latencies: KgClientLatencies = { lastQueryMs: 0 }
export function getKgClientLatencies(): KgClientLatencies {
  return { ...latencies }
}

/**
 * Local search over reusable KG entities (cogo:Capability / cogo:Action /
 * cogo:Event). Matches against rdfs:label / rdfs:comment via case-insensitive
 * substring (SPARQL CONTAINS + LCASE).
 *
 * Note on taxonomy: the Phase 2a Step 3 ontology shards (cogo-ns-v1.ttl +
 * events-v1.ttl + actions-v1.ttl + capabilities-v1.ttl) carry instances of
 * `cogo:Capability` / `cogo:Action` / `cogo:Event` but not `cogo:Component`
 * (component shapes are declared as NodeShapes, instance triples live in the
 * per-DS A-family shards which Step 5 does NOT load). We expose all three
 * reusable-entity classes here so "search_components" has meaningful behavior
 * against the Core shard. Phase 2b will tighten to only `cogo:Component`
 * instances once A-family DS shards are wired.
 *
 * `category` on the returned ComponentInfo is "capability" / "action" /
 * "event" so callers can route by subtype.
 *
 * Returns null if the KG path is unavailable (flag off or init failed) —
 * callers must route to the server fallback in that case.
 */
export async function localSearchComponents(query: string): Promise<ComponentInfo[] | null> {
  const ready = await ensureKgReady()
  const mod = getModule()
  if (!ready || !mod) return null

  const safe = query.replace(/"/g, '\\"').toLowerCase()
  const filter = safe.length === 0
    ? 'true'
    : `CONTAINS(LCASE(COALESCE(STR(?label), "")), "${safe}") ||
       CONTAINS(LCASE(COALESCE(STR(?comment), "")), "${safe}") ||
       CONTAINS(LCASE(STR(?iri)), "${safe}")`
  const sparql = `
    PREFIX rdf:  <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
    PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
    PREFIX cogo: <https://cogo.xyz/ns/v1#>
    SELECT ?iri ?label ?comment ?kind
    WHERE {
      {
        ?iri a cogo:Capability .
        BIND("capability" AS ?kind)
      } UNION {
        ?iri a cogo:Action .
        BIND("action" AS ?kind)
      } UNION {
        ?iri a cogo:Event .
        BIND("event" AS ?kind)
      }
      OPTIONAL { ?iri rdfs:label ?label . }
      OPTIONAL { ?iri rdfs:comment ?comment . }
      FILTER ( ${filter} )
    }
    ORDER BY ?kind ?iri
    LIMIT 50
  `
  const t0 = performance.now()
  const result = mod.queryShape(sparql)
  latencies.lastQueryMs = Math.round((performance.now() - t0) * 100) / 100

  return result.rows.map((row) => ({
    iri: row.iri,
    name: row.label || row.iri.split(/[/#]/).pop() || row.iri,
    category: row.kind,
    description: row.comment,
  }))
}

/**
 * Local lookup of a single reusable KG entity by IRI. Matches any rdf:type
 * of cogo:Capability / cogo:Action / cogo:Event (aligned with
 * localSearchComponents). Populates `category` with the matched subtype.
 *
 * Returns null if the KG path is unavailable OR if the IRI is not present in
 * the loaded shards. Callers should fall back to the server path on null.
 */
export async function localGetComponent(iri: string): Promise<ComponentInfo | null> {
  const ready = await ensureKgReady()
  const mod = getModule()
  if (!ready || !mod) return null

  const safe = iri.replace(/[<>]/g, '')
  const sparql = `
    PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
    PREFIX cogo: <https://cogo.xyz/ns/v1#>
    SELECT ?kind ?label ?comment
    WHERE {
      {
        <${safe}> a cogo:Capability .
        BIND("capability" AS ?kind)
      } UNION {
        <${safe}> a cogo:Action .
        BIND("action" AS ?kind)
      } UNION {
        <${safe}> a cogo:Event .
        BIND("event" AS ?kind)
      }
      OPTIONAL { <${safe}> rdfs:label ?label . }
      OPTIONAL { <${safe}> rdfs:comment ?comment . }
    }
    LIMIT 1
  `
  const t0 = performance.now()
  const result = mod.queryShape(sparql)
  latencies.lastQueryMs = Math.round((performance.now() - t0) * 100) / 100

  if (result.rows.length === 0) return null
  const row = result.rows[0]
  return {
    iri: safe,
    name: row.label || safe.split(/[/#]/).pop() || safe,
    category: row.kind,
    description: row.comment,
  }
}

/**
 * Local stats snapshot — counts + versionInfo.
 *
 * `totalComponents` reports `cogo:Capability` instance count (Core shard's
 * closest analogue to "reusable components" today — the per-DS A-family
 * shards that carry true `cogo:Component` instances are NOT loaded in the
 * Step 5 scope). Events + Actions report their direct instance counts.
 *
 * Returns null if the KG path is unavailable.
 */
export async function localGetStats(): Promise<ComponentStats | null> {
  const ready = await ensureKgReady()
  const mod = getModule()
  if (!ready || !mod) return null

  // Separate counts per subject to avoid cross-product blow-up on UNION.
  const sparql = `
    PREFIX cogo: <https://cogo.xyz/ns/v1#>
    SELECT
      (COUNT(DISTINCT ?c) AS ?components)
      (COUNT(DISTINCT ?e) AS ?events)
      (COUNT(DISTINCT ?a) AS ?actions)
    WHERE {
      { ?c a cogo:Capability . } UNION
      { ?e a cogo:Event .     } UNION
      { ?a a cogo:Action .    }
    }
  `
  const t0 = performance.now()
  const result = mod.queryShape(sparql)
  latencies.lastQueryMs = Math.round((performance.now() - t0) * 100) / 100

  const row = result.rows[0] ?? {}
  const v = mod.versionInfo()
  return {
    totalComponents: Number(row.components ?? 0),
    totalEvents: Number(row.events ?? 0),
    totalActions: Number(row.actions ?? 0),
    shapeSha: v.shapeSha,
    coreVersion: v.coreVersion,
    oxigraphVersion: v.oxigraphVersion,
  }
}

// ---------------------------------------------------------------------------
// Unified client: tries local first, falls back to server if null
// ---------------------------------------------------------------------------

/**
 * Callable shape for the server-side mcp-gateway fallback. Callers inject
 * their own gateway client — kg-client itself doesn't speak HTTP to the
 * gateway, keeping this module pure + testable.
 */
export interface KgServerFallback {
  searchComponents: (query: string) => Promise<ComponentInfo[]>
  getComponent: (iri: string) => Promise<ComponentInfo | null>
  getStats: () => Promise<ComponentStats | null>
}

export interface KgUnifiedClient {
  searchComponents: (query: string) => Promise<ComponentInfo[]>
  getComponent: (iri: string) => Promise<ComponentInfo | null>
  getStats: () => Promise<ComponentStats | null>
  /** Diagnostic — returns the most recent query latency + source. */
  getLastMetrics: () => {
    lastQueryMs: number
    lastSource: 'local' | 'server' | 'unknown'
    flagEnabled: boolean
  }
}

export function createKgClient(fallback: KgServerFallback): KgUnifiedClient {
  let lastSource: 'local' | 'server' | 'unknown' = 'unknown'

  return {
    async searchComponents(query: string): Promise<ComponentInfo[]> {
      if (isKgBrowserEnabled()) {
        const local = await localSearchComponents(query)
        if (local !== null) {
          lastSource = 'local'
          return local
        }
        // eslint-disable-next-line no-console
        console.debug('[kg-fallback] search_components — using server for query', query)
      }
      lastSource = 'server'
      return fallback.searchComponents(query)
    },

    async getComponent(iri: string): Promise<ComponentInfo | null> {
      if (isKgBrowserEnabled()) {
        const local = await localGetComponent(iri)
        if (local !== null) {
          lastSource = 'local'
          return local
        }
        // eslint-disable-next-line no-console
        console.debug('[kg-fallback] get_component — using server for iri', iri)
      }
      lastSource = 'server'
      return fallback.getComponent(iri)
    },

    async getStats(): Promise<ComponentStats | null> {
      if (isKgBrowserEnabled()) {
        const local = await localGetStats()
        if (local !== null) {
          lastSource = 'local'
          return local
        }
        // eslint-disable-next-line no-console
        console.debug('[kg-fallback] get_stats — using server')
      }
      lastSource = 'server'
      return fallback.getStats()
    },

    getLastMetrics() {
      return {
        lastQueryMs: latencies.lastQueryMs,
        lastSource,
        flagEnabled: isKgBrowserEnabled(),
      }
    },
  }
}

/**
 * Idle/no-op fallback for contexts where the server path is not yet wired up.
 * Returns empty results. Useful for the initial §5.3 migration where callers
 * want the local path but have no existing server caller to preserve.
 */
export const noopServerFallback: KgServerFallback = {
  async searchComponents() {
    return []
  },
  async getComponent() {
    return null
  },
  async getStats() {
    return null
  },
}
